import { Check, CircleDashed } from 'lucide-react'
import { ReactNode } from 'react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { wowheadUrl } from 'app/frontend/base/helpers'
import { TrinketChoice } from 'app/frontend/base/api/character'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import Select, { SelectProps } from 'app/frontend/base/components/Select'
import Card from 'app/frontend/base/components/Card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import ItemQuality from 'app/frontend/types/custom/ItemQuality'

export const TRINKET_CLEAR_VALUE = '__clear__'

type Props = {
  choice: TrinketChoice
  item?: {
    id: number
    name: string
    quality: ItemQuality
  }
  options: SelectProps['options']
  canEdit: boolean
  disabled?: boolean
  autoObtained: boolean
  effectiveObtained: boolean
  obtainedTooltip: string
  onChange: (choice: TrinketChoice) => void
  header?: ReactNode
  className?: string
}

const TrinketChoiceCard = ({
  choice,
  item,
  options,
  canEdit,
  disabled = false,
  autoObtained,
  effectiveObtained,
  obtainedTooltip,
  onChange,
  header,
  className,
}: Props) => {
  const {
    data: { gameVersion },
  } = usePageInfo()

  const setItem = (next: string | undefined) => {
    if (!next || next === TRINKET_CLEAR_VALUE) {
      onChange(null)
      return
    }

    const parsedId = parseInt(next)
    onChange({
      item_id: parsedId,
      obtained:
        choice?.item_id === parsedId ? choice?.obtained ?? false : false,
    })
  }

  const compact = !header

  const nameContent = (
    <Select
      value={choice?.item_id?.toString()}
      setValue={setItem}
      options={options}
      disabled={disabled || !canEdit}
      hideIcon={!!choice}
      isSearchable
      optionWidth='fit'
      emptyMessage='No trinkets available'
      placeholder='Not set'
      className={cn(
        'h-8 min-w-0 w-full max-w-full rounded-none border-0 py-0 shadow-none',
        choice
          ? 'justify-start bg-transparent px-0 text-sm font-medium hover:bg-transparent'
          : compact
          ? 'justify-center bg-transparent px-1 text-xs font-medium text-muted-foreground hover:bg-transparent'
          : 'justify-center bg-card px-2 text-xs font-medium text-muted-foreground hover:bg-card',
        item && `text-quality-${item.quality}`,
      )}
    />
  )

  const body = (
    <div className='flex h-8 w-full min-w-0 items-center'>
      <div className='flex min-w-0 flex-1 items-center gap-1 overflow-hidden px-1'>
        {choice ? (
          <a
            href={wowheadUrl(gameVersion, undefined, choice.item_id)}
            target='_blank'
            rel='noreferrer'
            className='shrink-0'
          >
            <ItemIcon id={choice.item_id} className='w-5 h-5' />
          </a>
        ) : null}
        <div className={cn('min-w-0 flex-1 overflow-hidden', choice && 'ml-1')}>
          {nameContent}
        </div>
      </div>

      {choice ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {canEdit && !autoObtained ? (
              <button
                type='button'
                aria-label={obtainedTooltip}
                aria-pressed={effectiveObtained}
                disabled={disabled}
                className={cn(
                  'flex h-8 shrink-0 items-center justify-center border-l px-2 transition-colors',
                  effectiveObtained
                    ? 'bg-success text-success-foreground hover:bg-success/80'
                    : 'text-muted-foreground hover:bg-muted',
                  disabled && 'pointer-events-none opacity-70',
                )}
                onClick={() =>
                  onChange({ ...choice, obtained: !choice.obtained })
                }
              >
                {effectiveObtained ? (
                  <Check className='h-4 w-4' />
                ) : (
                  <CircleDashed className='h-4 w-4' />
                )}
              </button>
            ) : (
              <span
                className={cn(
                  'flex h-8 shrink-0 items-center justify-center border-l px-2',
                  effectiveObtained
                    ? 'bg-success text-success-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {effectiveObtained ? (
                  <Check className='h-4 w-4' />
                ) : (
                  <CircleDashed className='h-4 w-4' />
                )}
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent>{obtainedTooltip}</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  )

  return (
    <Card
      className={cn(
        'overflow-hidden rounded-md border p-0 min-w-0',
        header
          ? 'flex w-full flex-col sm:min-w-0 sm:flex-1'
          : 'flex h-8 w-full max-w-full items-stretch',
        effectiveObtained && 'border-success-foreground',
        className,
      )}
    >
      {header ? (
        <div className='flex shrink-0 items-center justify-center border-b px-2 py-1.5 text-sm font-medium'>
          {header}
        </div>
      ) : null}
      {body}
    </Card>
  )
}

export default TrinketChoiceCard
